import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/context/LanguageContext";
import type { Therapist } from "@/types";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveUrl = (u: string) => (u && u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const TherapistGallery = () => {
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [lightbox, setLightbox] = useState<{ photos: string[]; alt: string; index: number } | null>(null);
  const { t, translateDescription } = useLanguage();

  const fetchTherapists = async () => {
    try {
      const response = await axios.get(`${API}/therapists`);
      setTherapists(response.data);
    } catch (error) {}
  };

  useEffect(() => {
    fetchTherapists();
    const interval = setInterval(fetchTherapists, 15000);
    return () => clearInterval(interval);
  }, []);

  const getDescription = (therapist: Therapist) => translateDescription(therapist.description || "");
  const isAvailable = (therapist: Therapist) => therapist.availability_status === "available";
  const getPhotos = (therapist: Therapist) => {
    const list = (therapist.photos && therapist.photos.length ? therapist.photos : [therapist.photo_url]).filter(Boolean);
    return list.map(resolveUrl);
  };

  const closeLightbox = useCallback(() => setLightbox(null), []);
  const step = useCallback((dir: number) => {
    setLightbox((l) => (l ? { ...l, index: (l.index + dir + l.photos.length) % l.photos.length } : l));
  }, []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, closeLightbox, step]);

  const openLightbox = (therapist: Therapist, index: number) =>
    setLightbox({ photos: getPhotos(therapist), alt: therapist.name, index });

  return (
    <section className="mb-16" data-testid="therapist-gallery">
      <h2 className="text-3xl font-bold text-center mb-2">{t("gallery.title")}</h2>
      <p className="text-center text-muted-foreground mb-8">{t("gallery.subtitle")}</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {therapists.map((therapist) => {
          const photos = getPhotos(therapist);
          const multi = photos.length > 1;
          return (
            <Card key={therapist.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`therapist-card-${therapist.id}`}>
              <CardContent className="p-0">
                <button
                  type="button"
                  onClick={() => openLightbox(therapist, 0)}
                  className="group block w-full aspect-square overflow-hidden relative bg-slate-100 cursor-zoom-in"
                  data-testid={`therapist-photo-btn-${therapist.id}`}
                  aria-label={`View ${therapist.name} photos`}
                >
                  <img
                    src={photos[0]}
                    alt={therapist.name}
                    className={`w-full h-full ${multi ? "object-contain" : "object-cover"} transition-transform duration-300 group-hover:scale-105`}
                    data-testid={`therapist-photo-${therapist.id}`}
                  />
                  <span className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-black/55 px-2 py-1 text-[10px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                    <ZoomIn className="w-3 h-3" /> {t("gallery.viewPhotos")}
                  </span>
                  <span
                    data-testid={`therapist-status-${therapist.id}`}
                    className={`absolute top-2 right-2 w-4 h-4 rounded-full border-2 border-white shadow-md ${
                      isAvailable(therapist) ? "bg-green-500" : "bg-red-500"
                    }`}
                    title={isAvailable(therapist) ? t("common.available") : t("common.unavailable")}
                  />
                </button>

                {multi && (
                  <div className="flex gap-1.5 px-3 pt-3" data-testid={`therapist-thumbs-${therapist.id}`}>
                    {photos.map((p, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => openLightbox(therapist, i)}
                        className="h-12 w-12 overflow-hidden rounded-md border-2 border-transparent bg-slate-100 transition-colors hover:border-purple-400"
                        data-testid={`therapist-thumb-${therapist.id}-${i}`}
                        aria-label={`View photo ${i + 1} of ${therapist.name}`}
                      >
                        <img src={p} alt={`${therapist.name} ${i + 1}`} className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}

                <div className="p-4 text-center">
                  <h3 className="font-semibold text-lg" data-testid={`therapist-name-${therapist.id}`}>{therapist.name}</h3>
                  {getDescription(therapist) && (
                    <p className="text-sm text-muted-foreground mt-1" data-testid={`therapist-desc-${therapist.id}`}>{getDescription(therapist)}</p>
                  )}
                  <p className={`text-xs font-medium mt-1 ${isAvailable(therapist) ? "text-green-600" : "text-red-500"}`}>
                    {isAvailable(therapist) ? t("common.available") : t("common.unavailable")}
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={closeLightbox}
          data-testid="therapist-lightbox"
        >
          <button
            onClick={closeLightbox}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
            data-testid="therapist-lightbox-close"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>

          {lightbox.photos.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-2 sm:left-6 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
              data-testid="therapist-lightbox-prev"
              aria-label="Previous photo"
            >
              <ChevronLeft className="h-7 w-7" />
            </button>
          )}

          <img
            src={lightbox.photos[lightbox.index]}
            alt={lightbox.alt}
            className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="therapist-lightbox-image"
          />

          {lightbox.photos.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-2 sm:right-6 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
              data-testid="therapist-lightbox-next"
              aria-label="Next photo"
            >
              <ChevronRight className="h-7 w-7" />
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default TherapistGallery;
